import PropTypes from "prop-types"
import { map } from "lodash"
import classnames from "classnames"
import { CardContent, Paper } from "@mui/material"
import { DATE } from "helpers/shapes"
import { getTrendsExposureFormatters } from "helpers/formatters/trends"
import { AGGREGATION_SHAPE } from "helpers/factorClassification"
import { FACTOR_TAB_OPTION_SHAPE } from "helpers/compositeSecurities"
import { computeCSVData } from "helpers/csv"
import { useTrack } from "helpers/analytics"
import NoData from "components/no-data"
import GraphTopBar from "components/graph-top-bar"
import AggregationDropdown, { OPTION_SHAPE } from "components/list-item-dropdown"
import MultiLine from "components/charts/multi-line"
import Tooltip from "components/analyze-tooltip"
import ExcelDownload from "components/excel-download"
import HistoricalExposureDetails from "modules/analyze-composite-exposure-details"
import DefaultMetricUnits from "modules/default-metric-units"
import { METRIC_TYPES_SHAPE } from "helpers/metrics/shapes"
import { METRIC_TYPES } from "helpers/assets/constants"

import "./style.scss"

export default function AnalyzeCompositeExposure({
  account,
  modelId,
  analyzeViewSelectList,
  data,
  exposure,
  items,
  segment,
  positionSetId,
  positionSetType,
  positionSetName,
  classifications,
  filtersVisible,
  startDate,
  selectedDate,
  endDate,
  earliestDate,
  latestDate,
  setDate,
  drift,
  contentSetId,
  openDialog,
  options,
  selectedOption,
  factorCategoryId,
  factorCategoryName,
  allCategoryItems,
  viewType,
  currentGrouping,
  expand,
  displayAddView,
  selectedViews,
  addSelectedView,
  removeSelectedView,
  selectFactorCategory,
  availableSelectViewFactors,
  selectedViewsFactors,
  aggregation,
  mustAggregateFactors,
  factorsClassification,
  aggregationOptions,
  displayAggregationToggle,
  setAggregationFunction,
  focusedChartLine,
  focusChartLine,
  blurChartLine,
  only,
  fileName,
  showNotHeld,
  base,
  baseSignature,
  setGrouping,
  toggle,
  toggleOnly,
  currency,
  metricType,
  setMetricType,
}) {
  const trackExcelDownload = useTrack({
    type: "composite-analyze-exposure-graph-timeseries-download",
  })

  let content
  const useCurrencyExposure = metricType === METRIC_TYPES.CURRENCY_EXPOSURE
  const { detailsFormatter, tickFormat, tooltipFormatter } = getTrendsExposureFormatters(useCurrencyExposure, currency)

  if (!earliestDate || !latestDate) {
    content = <NoData title="Exposures" className="graph" />
  } else {
    content = <CardContent className={classnames("card-content", { aggregation: displayAggregationToggle })}>
      <Paper className="content">
        <div className="graph-container">
          <div className="graph-top">
            {analyzeViewSelectList}
            <GraphTopBar {...{
              openDialog,
              displayAddView,
              addView: ({ id }) => addSelectedView(id),
              factors: availableSelectViewFactors,
              value: factorCategoryId,
              onTabChanged: (_, factorId) => selectFactorCategory(factorId),
              tabs: map(options, ({ id, label, isSelectedView }) => ({
                key: id,
                value: id,
                removable: isSelectedView,
                onRemoveTab: (factorId) => removeSelectedView(factorId, options),
                label,
              })),
              actions: [
                !contentSetId && <DefaultMetricUnits key="unit-selector" metric="exposure" disabled unit={metricType} onChange={setMetricType} includeUnitSelector />,
                <ExcelDownload key="excel-download" data={computeCSVData(data, items, { percents: true })} fileName={fileName} track={trackExcelDownload} />,
              ],
            }}
            />
          </div>
          <div className="graph">
            {displayAggregationToggle && <AggregationDropdown setValue={setAggregationFunction} value={aggregation} listSubheader="Choose display type for style factors" label="Display Type" options={aggregationOptions} classes={{ toggle: "aggregation-toggle" }} />}
            <MultiLine
              tooltip={<Tooltip data={data} items={items} focusedChartLine={focusedChartLine} formatter={tooltipFormatter} />}
              {...{
                ...tickFormat,
                startDate,
                date: selectedDate,
                setDate,
                endDate,
                data,
                onlyItem: only,
                items,
                toggle,
                focusChartLine,
                blurChartLine,
                focusedChartLine,
                toggleOnly: (factorId) => toggleOnly(factorId, only !== factorId, items, contentSetId),
              }}
            />
          </div>
        </div>
        <div className="contributors">
          <HistoricalExposureDetails
            {...{
              account,
              modelId,
              positionSetId,
              positionSetType,
              positionSetName,
              exposure,
              segment,
              aggregation,
              mustAggregateFactors,
              factorsClassification,
              classifications,
              selectedDate,
              viewType,
              selectedViewsFactors,
              expand,
              currentGrouping,
              setGrouping,
              displayType: factorCategoryId,
              factorCategoryName,
              allCategoryItems,
              startDate,
              endDate,
              drift,
              selectedOption,
              contentSetId,
              selectedViews,
              filtersVisible,
              showNotHeld,
              base,
              baseSignature,
              metricType,
              formatter: detailsFormatter,
            }}
          />
        </div>
      </Paper>
    </CardContent>
  }

  return (
    <div className="modules_analyze-composite-exposure">
      {content}
    </div>
  )
}

AnalyzeCompositeExposure.propTypes = {
  analyzeViewSelectList: PropTypes.element,
  account: PropTypes.string.isRequired,
  modelId: PropTypes.string.isRequired,
  positionSetId: PropTypes.string.isRequired,
  positionSetType: PropTypes.string.isRequired,
  positionSetName: PropTypes.string.isRequired,
  contentSetId: PropTypes.string,
  data: PropTypes.array.isRequired,
  exposure: PropTypes.array.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    disabled: PropTypes.bool.isRequired,
  })).isRequired,
  currentGrouping: PropTypes.object,
  setGrouping: PropTypes.func.isRequired,
  classifications: PropTypes.array,
  categories: PropTypes.array,
  allCategoryItems: PropTypes.array.isRequired,
  expand: PropTypes.bool,
  startDate: DATE.isRequired,
  firstDate: DATE,
  selectedDate: DATE,
  endDate: DATE,
  earliestDate: DATE,
  latestDate: DATE,
  setDate: PropTypes.func.isRequired,
  driftDates: PropTypes.object.isRequired,
  drift: PropTypes.object.isRequired,
  openDialog: PropTypes.func.isRequired,
  filtersVisible: PropTypes.bool.isRequired,
  displayAggregationToggle: PropTypes.bool,
  mustAggregateFactors: PropTypes.bool,
  factorsClassification: PropTypes.object,
  favoriteFactors: PropTypes.object,
  aggregatedFactorsMetadata: PropTypes.object,
  displayAddView: PropTypes.bool.isRequired,
  options: PropTypes.arrayOf(FACTOR_TAB_OPTION_SHAPE).isRequired,
  selectedOption: PropTypes.object.isRequired,
  factorCategoryId: PropTypes.string.isRequired,
  factorCategoryName: PropTypes.string.isRequired,
  viewType: PropTypes.string.isRequired,
  selectedViews: PropTypes.array,
  addSelectedView: PropTypes.func.isRequired,
  removeSelectedView: PropTypes.func.isRequired,
  selectFactorCategory: PropTypes.func.isRequired,
  availableSelectViewFactors: PropTypes.array.isRequired,
  selectedViewsFactors: PropTypes.array,
  only: PropTypes.string,
  aggregation: AGGREGATION_SHAPE,
  setAggregationFunction: PropTypes.func.isRequired,
  aggregationOptions: PropTypes.arrayOf(OPTION_SHAPE),
  focusChartLine: PropTypes.func.isRequired,
  blurChartLine: PropTypes.func.isRequired,
  focusedChartLine: PropTypes.string,
  fileName: PropTypes.string.isRequired,
  segment: PropTypes.object,
  showNotHeld: PropTypes.bool.isRequired,
  base: PropTypes.object,
  baseSignature: PropTypes.string,
  toggle: PropTypes.func.isRequired,
  toggleOnly: PropTypes.func.isRequired,
  currency: PropTypes.string,
  metricType: METRIC_TYPES_SHAPE.isRequired,
  setMetricType: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
}
